import React, {useEffect, useState} from "react";
import {Stack, Box, Paper, CircularProgress} from "@mui/material";
import CustomButton from "../../../core/components/ui/CustomButton";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {useNavigate, useLocation} from "react-router-dom";
import useBooking from "../hooks/useBooking";
import CustomTextField from "../../../core/components/ui/CustomTextfield";
import BookingTypesDropdownComponent from "../common/booking-types-dropdown.component";
import CustomDatePicker from "../../../core/components/common/custom-datepicker.component";
import dayjs from "dayjs";

const EditBookingComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const {bookingDetailsData, viewBooking, isUpdatingBooking, updateBooking} = useBooking();
    
    const [purposeInput, setPurposeInput] = useState("");
    const [selectedBookingType, setSelectedBookingType] = useState(null);
    const [scheduleIn, setScheduleIn] = useState(null);
    const [scheduleOut, setScheduleOut] = useState(null);

    const bookingId = new URLSearchParams(location.search).get("id");
    const rbacStorage = JSON.parse(localStorage.getItem("rbac-storage"));
    const storedOrgIdFromRBACForBusiness = rbacStorage?.state?.currentProfile?.orgId || null;
    const orgIdFromUrlForPortEmployee = new URLSearchParams(location.search).get("org_uid");


    useEffect(() => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee;

        if (organizationId) {
            const parameters = {
                bookingUID: bookingId,
                orgUID: organizationId
            };
    
            viewBooking(parameters);
    
        } else {
            const parameters = {bookingUID: bookingId};
            viewBooking(parameters);
        }
    }, [bookingId, storedOrgIdFromRBACForBusiness, orgIdFromUrlForPortEmployee]);

    useEffect(() => {
        if (bookingDetailsData) {
            setPurposeInput(bookingDetailsData?.purpose);
            setSelectedBookingType(bookingDetailsData?.booking_type);
            setScheduleIn(dayjs(bookingDetailsData?.valid_from));
            setScheduleOut(dayjs(bookingDetailsData?.valid_to));
        }
    }, [bookingDetailsData])


    const handleBookingTypeChange = (value) => {
        setSelectedBookingType(value);
    }

    const handleTextfieldChange = (e) => {
        setPurposeInput(e.target.value);
    }

    const handleBack = () => {
        if (orgIdFromUrlForPortEmployee) {
            navigate(`/organization/view?org_uid=${orgIdFromUrlForPortEmployee}`);

        } else {
            navigate("/bookings");
        }
    }

    const handleEditBooking = () => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee;

        if (organizationId) {
            const parameters = {
                bookingUID: bookingDetailsData?.booking_uid,
                orgUID: organizationId
            };

            const payload = {
                booking_type: selectedBookingType,
                purpose: purposeInput,
                valid_from: dayjs(new Date(scheduleIn)).format("YYYY-MM-DD"),
                valid_to: dayjs(new Date(scheduleOut)).format("YYYY-MM-DD")
            };

            updateBooking(parameters, payload, orgIdFromUrlForPortEmployee);

        } else if (!organizationId) {
            const parameters = {
                bookingUID: bookingDetailsData?.booking_uid
            };

            const payload = {
                booking_type: selectedBookingType,
                purpose: purposeInput,
                valid_from: dayjs(new Date(scheduleIn)).format("YYYY-MM-DD"),
                valid_to: dayjs(new Date(scheduleOut)).format("YYYY-MM-DD")
            };

            updateBooking(parameters, payload);
        }
    }


    return (
        <>  
            <CustomButton 
                variant="text" 
                startIcon={<ArrowBackIcon fontSize="small" />}
                label="Back to Booking List"
                customStyles={{fontSize: 14, ml: -2}}
                onClick={handleBack}
            />

            <Box sx={{m: "auto", maxWidth: "1000px"}}>  
                <Box sx={{textAlign: "start", mt: 2}}>
                    <CustomTypography 
                        variant="h3"
                        text={"Edit Booking Request"}
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />

                    <CustomTypography 
                        variant="subtitle1"
                        text={"Update booking request for entry"}
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />
                </Box>

                <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px 40px", mt: 3}}>
                    <Box sx={{textAlign: "start", mb: 5}}>
                        <CustomTypography 
                            variant="h3"
                            text={"Booking Details"}
                            customStyles={{
                                marginBottom: 0, 
                                fontSize: 22,
                                fontWeight: 600
                            }}
                        />

                        <CustomTypography 
                            variant="caption"
                            text={"Fill in the details to update booking request"}
                            customStyles={{
                                marginBottom: 0,
                                fontSize: 14 
                            }}
                        />
                    </Box>

                    <Stack direction={{xs: "column", md: "row"}} spacing={4}>
                        <Stack spacing={2} flex={1}>
                            <Box>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Booking Type"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                />

                                <BookingTypesDropdownComponent 
                                    value={selectedBookingType}
                                    onChange={handleBookingTypeChange}
                                    fullWidth={true}
                                    size={"medium"}
                                    disabled={isUpdatingBooking}
                                />
                            </Box>

                            <Box sx={{mt: "32px !important"}}>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Schedule In"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                />
                                <Box>
                                    <CustomDatePicker
                                        label="Schedule In"
                                        value={scheduleIn}
                                        onChange={(newDate) => setScheduleIn(newDate)}
                                        disabled={isUpdatingBooking}
                                    />
                                </Box>
                            </Box>
                        </Stack>

                        <Stack spacing={2} flex={1}>
                            <Box>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Purpose"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                /> 
                        
                                <CustomTextField 
                                    placeholder={"Enter purpose"}
                                    variant={"outlined"}
                                    value={purposeInput}
                                    onChange={(e) => handleTextfieldChange(e)}
                                    fullWidth={true}
                                    multiline={false}
                                    hiddenLabel
                                    readOnly={isUpdatingBooking}
                                />           
                            </Box>

                            <Box sx={{mt: "32px !important"}}>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Schedule Out"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                />
                                <Box>
                                    <CustomDatePicker
                                        label="Schedule Out"
                                        value={scheduleOut}
                                        onChange={(newDate) => setScheduleOut(newDate)}
                                        disabled={isUpdatingBooking}
                                        minDate={scheduleIn}
                                    />
                                </Box>
                            </Box>
                        </Stack>
                    </Stack>   

                    <Stack direction={{xs: "column", md: "row"}} spacing={2} sx={{mt: 7, justifyContent: "end"}}>
                        <CustomButton 
                            variant={"outlined"}
                            label={"Cancel"}
                            customStyles={{}}
                            onClick={(isUpdatingBooking) ? null : handleBack}
                        />     

                        <CustomButton 
                            variant={"contained"}
                            label={"Update Booking"}
                            startIcon={isUpdatingBooking && <CircularProgress size="15px" color="#fff" sx={{mr: "2px"}} />}
                            customStyles={{
                                opacity: (purposeInput && selectedBookingType && scheduleIn && scheduleOut && 
                                    (
                                        (purposeInput !== bookingDetailsData?.purpose) ||
                                        (selectedBookingType !== bookingDetailsData?.booking_type) ||
                                        (dayjs(new Date(scheduleIn)).format("YYYY-MM-DD") !== dayjs(bookingDetailsData?.valid_from).format("YYYY-MM-DD")) ||
                                        (dayjs(new Date(scheduleOut)).format("YYYY-MM-DD") !== dayjs(bookingDetailsData?.valid_to).format("YYYY-MM-DD"))
                                    ) && !isUpdatingBooking) ? "100%" : "55%",
                                cursor: (purposeInput && selectedBookingType && scheduleIn && scheduleOut && 
                                    (
                                        purposeInput !== bookingDetailsData?.purpose ||
                                        selectedBookingType !== bookingDetailsData?.booking_type ||
                                        dayjs(new Date(scheduleIn)).format("YYYY-MM-DD") !== dayjs(bookingDetailsData?.valid_from).format("YYYY-MM-DD") ||
                                        dayjs(new Date(scheduleOut)).format("YYYY-MM-DD") !== dayjs(bookingDetailsData?.valid_to).format("YYYY-MM-DD")
                                    ) && !isUpdatingBooking) ? "pointer" : "not-allowed !important",
                            }}
                            onClick={(purposeInput && selectedBookingType && scheduleIn && scheduleOut && 
                                (
                                    purposeInput !== bookingDetailsData?.purpose ||
                                    selectedBookingType !== bookingDetailsData?.booking_type ||
                                    dayjs(new Date(scheduleIn)).format("YYYY-MM-DD") !== dayjs(bookingDetailsData?.valid_from).format("YYYY-MM-DD") ||
                                    dayjs(new Date(scheduleOut)).format("YYYY-MM-DD") !== dayjs(bookingDetailsData?.valid_to).format("YYYY-MM-DD")
                                ) && !isUpdatingBooking) ? handleEditBooking : null}
                        /> 
                    </Stack>  
                </Paper>
            </Box>
       </>
    )
}

export default EditBookingComponent